import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle, Loader2, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { Project, CompanySummary, MarkAsPaidCalculations, DistributionData, ProjectSnapshotData, KickerEligiblePerson, PerformanceKicker, TacticalSpecialist } from '@/types/project';
import { projectAPI } from '@/utils/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface MarkAsPaidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  month: string; // YYYY-MM format
  projects: Project[];
  companySummary: CompanySummary;
  onSuccess?: () => void;
}

const STRATEGY_PARTNERS = ['Ken', 'Sean', 'Tim', 'Trav', 'Chase'];

export function MarkAsPaidDialog({
  open,
  onOpenChange,
  month,
  projects,
  companySummary,
  onSuccess,
}: MarkAsPaidDialogProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffCycle, setIsOffCycle] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(month);

  // Rolling window of selectable months (2 ahead → 15 back), always including the active month.
  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    const base = new Date();
    for (let i = -2; i <= 15; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    if (selectedMonth) set.add(selectedMonth);
    return Array.from(set).sort().reverse();
  }, [selectedMonth]);

  // Get all active projects
  const allActiveProjects = useMemo(() => projects.filter(p => p.status === 'active'), [projects]);

  // Track selected project IDs - default to all active projects
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());

  // Initialize selected projects and month when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedProjectIds(new Set(allActiveProjects.map(p => p.id)));
      setSelectedMonth(month);
      setIsOffCycle(false);
    }
  }, [open, allActiveProjects, month]);

  // Get selected projects
  const selectedProjects = useMemo(() =>
    allActiveProjects.filter(p => selectedProjectIds.has(p.id)),
    [allActiveProjects, selectedProjectIds]
  );

  // Toggle a single project
  const toggleProject = (projectId: string) => {
    setSelectedProjectIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedProjectIds.size === allActiveProjects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(allActiveProjects.map(p => p.id)));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatMonth = (monthYear: string) => {
    const [year, monthNum] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Helper to resolve eligible people from new or legacy kicker format
  const resolveKickerEligiblePeople = (
    kicker: PerformanceKicker,
    tacticalSpecialists: TacticalSpecialist[],
    operationsOwners: string[]
  ): KickerEligiblePerson[] => {
    // Use new format if available
    if (kicker.eligiblePeople && kicker.eligiblePeople.length > 0) {
      return kicker.eligiblePeople;
    }

    // Fall back to legacy format
    const eligible: KickerEligiblePerson[] = [];

    // Convert assignedSpecialistIds to eligible people
    (kicker.assignedSpecialistIds || []).forEach(specialistId => {
      const specialist = tacticalSpecialists.find(s => s.id === specialistId);
      if (specialist) {
        const name = specialist.assignedOwner || specialist.name;
        const isOwner = STRATEGY_PARTNERS.includes(name);
        if (!eligible.some(p => p.name === name)) {
          eligible.push({
            name,
            type: isOwner ? 'owner' : 'external',
            role: specialist.name
          });
        }
      }
    });

    // Add operations owners if eligible
    if (kicker.operationsOwnersEligible) {
      operationsOwners.forEach(owner => {
        if (!eligible.some(p => p.name === owner)) {
          eligible.push({
            name: owner,
            type: STRATEGY_PARTNERS.includes(owner) ? 'owner' : 'external',
            role: 'Operations'
          });
        }
      });
    }

    return eligible;
  };

  // Resolve revenue for a project: variable projects use ad-hoc amount for the selected month
  const getProjectRevenue = (project: Project): number => {
    if (project.revenueType === 'variable') {
      const forecast = project.adHocForecasts?.find(f => f.month === selectedMonth);
      return forecast?.amount || 0;
    }
    return project.baseTemplate.revenue;
  };

  // Build the calculations payload for the API based on SELECTED projects only
  const calculations = useMemo((): MarkAsPaidCalculations => {
    const projectSnapshots: ProjectSnapshotData[] = selectedProjects
      .map(project => {
        const base = project.baseTemplate;
        const revenue = getProjectRevenue(project);
        const profitMarginDecimal = base.profitMargin / 100;

        // Calculate costs
        const operationsCompensationPerOwner = base.operationsCompensationPerOwner || 2500;
        const operationsCompensation = base.operationsOwners.length * operationsCompensationPerOwner;
        const operationsOverhead = revenue * (base.operationsOverheadPercentage / 100);
        const operationsCost = operationsOverhead + operationsCompensation;

        const tacticalCost = base.tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);

        // Kicker calculations (simplified - using base template)
        let kickerRevenue = 0;
        let kickerCost = 0;
        const KICKER_TIERS: Record<string, number> = {
          'none': 0,
          'tier1': 0.05,
          'tier2': 0.075,
          'tier3': 0.10,
        };

        base.performanceKickers?.forEach(kicker => {
          const percentage = KICKER_TIERS[kicker.selectedTier] || 0;
          if (percentage > 0) {
            const kr = kicker.marketingSpend * percentage;
            kickerRevenue += kr;

            // Use new simplified model
            const eligiblePeople = resolveKickerEligiblePeople(kicker, base.tacticalSpecialists, base.operationsOwners);
            const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;

            kickerCost += kr * (sharePercentage / 100) * eligiblePeople.length;
          }
        });

        const grossProfit = revenue + kickerRevenue - operationsCost - tacticalCost - kickerCost;
        const netProfit = revenue * profitMarginDecimal;

        return {
          legacyId: project.id,
          projectName: project.projectName,
          status: project.status,
          revenue,
          profitMargin: base.profitMargin,
          operationsOverheadPercentage: base.operationsOverheadPercentage,
          operationsCompensationPerOwner,
          grossProfit,
          netProfit,
          operationsCost,
          tacticalCost,
          kickerRevenue,
          kickerCost,
          fullConfig: {
            baseTemplate: base,
            projectName: project.projectName,
            status: project.status,
          },
        };
      });

    // Calculate totals and distributions from SELECTED projects only
    const ownerDistributions: Record<string, number> = {};
    const ownerOperationsEarnings: Record<string, number> = {};
    const ownerTacticalEarnings: Record<string, number> = {};
    const ownerKickerEarnings: Record<string, number> = {};
    // Track external contractor kicker payouts
    const externalKickerPayouts: Array<{
      name: string;
      role?: string;
      projectName: string;
      kickerName: string;
      amount: number;
    }> = [];
    // Track non-owner specialist payouts (tactical + ops)
    const specialistTacticalPayouts: Array<{
      name: string;
      role: string;
      projectName: string;
      amount: number;
      distributionType: 'tactical_compensation' | 'operations_compensation';
    }> = [];

    // Initialize
    STRATEGY_PARTNERS.forEach(owner => {
      ownerDistributions[owner] = 0;
      ownerOperationsEarnings[owner] = 0;
      ownerTacticalEarnings[owner] = 0;
      ownerKickerEarnings[owner] = 0;
    });

    let totalRevenue = 0;
    let totalProfit = 0;
    let totalOperatingCosts = 0;
    let totalKickerRevenue = 0;

    // Process each selected project
    selectedProjects.forEach(project => {
      const base = project.baseTemplate;
      const revenue = getProjectRevenue(project);
      const profitMarginDecimal = base.profitMargin / 100;

      // Calculate costs
      const operationsCompensationPerOwner = base.operationsCompensationPerOwner || 2500;
      const operationsCompensation = base.operationsOwners.length * operationsCompensationPerOwner;
      const operationsOverhead = revenue * (base.operationsOverheadPercentage / 100);
      const operationsCost = operationsOverhead + operationsCompensation;
      const tacticalCost = base.tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);

      // Kicker calculations - using new simplified model
      let projectKickerRevenue = 0;
      let projectKickerCost = 0;
      const KICKER_TIERS: Record<string, number> = {
        'none': 0,
        'tier1': 0.05,
        'tier2': 0.075,
        'tier3': 0.10,
      };

      base.performanceKickers?.forEach(kicker => {
        const percentage = KICKER_TIERS[kicker.selectedTier] || 0;
        if (percentage > 0) {
          const kr = kicker.marketingSpend * percentage;
          projectKickerRevenue += kr;

          // Use new simplified model
          const eligiblePeople = resolveKickerEligiblePeople(kicker, base.tacticalSpecialists, base.operationsOwners);
          const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;
          const perPersonPayout = kr * (sharePercentage / 100);

          // Distribute to eligible people
          eligiblePeople.forEach(person => {
            if (person.type === 'owner' && STRATEGY_PARTNERS.includes(person.name)) {
              // Owner gets tracked in owner kicker earnings
              ownerKickerEarnings[person.name] += perPersonPayout;
            } else if (person.type === 'external') {
              // Track external contractor payouts
              externalKickerPayouts.push({
                name: person.name,
                role: person.role,
                projectName: project.projectName,
                kickerName: kicker.name,
                amount: perPersonPayout,
              });
            }
          });
          projectKickerCost += perPersonPayout * eligiblePeople.length;

          // Remaining goes to all owners equally
          const remainingForOwners = kr - (perPersonPayout * eligiblePeople.length);
          const perOwner = remainingForOwners / STRATEGY_PARTNERS.length;
          STRATEGY_PARTNERS.forEach(owner => {
            ownerKickerEarnings[owner] += perOwner;
          });
        }
      });

      // Accumulate totals
      totalRevenue += revenue + projectKickerRevenue;
      totalKickerRevenue += projectKickerRevenue;
      totalOperatingCosts += operationsCost + tacticalCost + projectKickerCost;

      // Profit distribution (base profit share)
      const baseOwnerDistribution = revenue * profitMarginDecimal;
      totalProfit += baseOwnerDistribution;
      const perOwnerShare = baseOwnerDistribution / STRATEGY_PARTNERS.length;
      STRATEGY_PARTNERS.forEach(owner => {
        ownerDistributions[owner] += perOwnerShare;
      });

      // Operations compensation distribution
      base.operationsOwners.forEach(owner => {
        if (STRATEGY_PARTNERS.includes(owner)) {
          ownerOperationsEarnings[owner] += operationsCompensationPerOwner;
        } else {
          // Non-owner ops member (e.g., Megan) — track their ops payout
          specialistTacticalPayouts.push({
            name: owner,
            role: 'Operations Manager',
            projectName: project.projectName,
            amount: operationsCompensationPerOwner,
            distributionType: 'operations_compensation',
          });
        }
      });

      // Tactical compensation distribution
      base.tacticalSpecialists.forEach(spec => {
        if (spec.assignedOwner && STRATEGY_PARTNERS.includes(spec.assignedOwner)) {
          ownerTacticalEarnings[spec.assignedOwner] += spec.amount;
        } else if (spec.assignedOwner && !STRATEGY_PARTNERS.includes(spec.assignedOwner)) {
          // Non-owner specialist (e.g., Megan, Konnor) — track their payout
          specialistTacticalPayouts.push({
            name: spec.assignedOwner,
            role: spec.name,
            projectName: project.projectName,
            amount: spec.amount,
            distributionType: 'tactical_compensation',
          });
        }
      });
    });

    // Build distribution records
    const distributions: DistributionData[] = [];

    STRATEGY_PARTNERS.forEach(owner => {
      if (ownerDistributions[owner] > 0) {
        distributions.push({
          recipientType: 'owner',
          recipientName: owner,
          type: 'profit_share',
          amount: ownerDistributions[owner],
        });
      }
      if (ownerOperationsEarnings[owner] > 0) {
        distributions.push({
          recipientType: 'owner',
          recipientName: owner,
          type: 'operations_compensation',
          amount: ownerOperationsEarnings[owner],
        });
      }
      if (ownerTacticalEarnings[owner] > 0) {
        distributions.push({
          recipientType: 'owner',
          recipientName: owner,
          type: 'tactical_compensation',
          amount: ownerTacticalEarnings[owner],
        });
      }
      if (ownerKickerEarnings[owner] > 0) {
        distributions.push({
          recipientType: 'owner',
          recipientName: owner,
          type: 'kicker_bonus',
          amount: ownerKickerEarnings[owner],
        });
      }
    });

    // Add external kicker payouts to distributions
    externalKickerPayouts.forEach(payout => {
      distributions.push({
        recipientType: 'specialist',
        recipientName: payout.name,
        type: 'external_kicker_payout',
        amount: payout.amount,
        projectName: payout.projectName,
        roleName: payout.role,
        kickerName: payout.kickerName,
      });
    });

    // Add non-owner specialist payouts to distributions
    specialistTacticalPayouts.forEach(payout => {
      distributions.push({
        recipientType: 'specialist',
        recipientName: payout.name,
        type: payout.distributionType,
        amount: payout.amount,
        projectName: payout.projectName,
        roleName: payout.role,
      });
    });

    return {
      totalRevenue,
      totalProfit,
      totalOperatingCosts,
      totalKickerRevenue,
      projectSnapshots,
      distributions,
      // Also return calculated owner earnings for UI
      ownerDistributions,
      ownerOperationsEarnings,
      ownerTacticalEarnings,
      ownerKickerEarnings,
      // External contractor payouts for display
      externalKickerPayouts,
      specialistTacticalPayouts,
    };
  }, [selectedProjects, selectedMonth]);

  // Calculate total per owner based on selected projects
  const ownerTotals = useMemo(() => {
    return STRATEGY_PARTNERS.map(owner => ({
      name: owner,
      profitShare: calculations.ownerDistributions?.[owner] || 0,
      operations: calculations.ownerOperationsEarnings?.[owner] || 0,
      tactical: calculations.ownerTacticalEarnings?.[owner] || 0,
      kicker: calculations.ownerKickerEarnings?.[owner] || 0,
      total:
        (calculations.ownerDistributions?.[owner] || 0) +
        (calculations.ownerOperationsEarnings?.[owner] || 0) +
        (calculations.ownerTacticalEarnings?.[owner] || 0) +
        (calculations.ownerKickerEarnings?.[owner] || 0),
    }));
  }, [calculations]);

  const handleMarkAsPaid = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await projectAPI.markMonthAsPaid(selectedMonth, user?.email || 'unknown', calculations, isOffCycle);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as paid');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg font-serif">
            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
            Mark {formatMonth(selectedMonth)} as Paid
            {isOffCycle && (
              <Badge variant="secondary" className="ml-2 text-xs">Off-Cycle</Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Select which projects to include in this payout.
          </DialogDescription>
        </DialogHeader>

        {/* Payment Month & Off-Cycle */}
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
            <div>
              <label htmlFor="payment-month" className="text-sm font-medium cursor-pointer">
                Payment Month
              </label>
              <p className="text-xs text-muted-foreground">
                Select which month this payment is for
              </p>
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger id="payment-month" size="sm" className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((ym) => (
                  <SelectItem key={ym} value={ym}>
                    {formatMonth(ym)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
            <div>
              <label htmlFor="off-cycle-toggle" className="text-sm font-medium cursor-pointer">
                Off-Cycle Payout
              </label>
              <p className="text-xs text-muted-foreground">
                Mark this as an additional mid-month distribution
              </p>
            </div>
            <Switch
              id="off-cycle-toggle"
              checked={isOffCycle}
              onCheckedChange={setIsOffCycle}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-2 sm:p-3 bg-destructive/10 text-destructive rounded-md text-xs sm:text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          {/* Project Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs sm:text-sm font-medium">
                Select Projects ({selectedProjects.length} of {allActiveProjects.length})
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                className="h-7 text-xs"
              >
                {selectedProjectIds.size === allActiveProjects.length ? (
                  <>
                    <Square className="h-3 w-3 mr-1" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <CheckSquare className="h-3 w-3 mr-1" />
                    Select All
                  </>
                )}
              </Button>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto border rounded-md p-2">
              {allActiveProjects.map(project => {
                const isSelected = selectedProjectIds.has(project.id);
                return (
                  <div
                    key={project.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => toggleProject(project.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleProject(project.id)}
                      className="pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {project.projectName}
                        {project.revenueType === 'variable' && (
                          <span className="ml-1 text-xs text-muted-foreground">(variable)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(getProjectRevenue(project))}{project.revenueType === 'variable' ? ' ad-hoc' : '/mo'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedProjects.length === 0 && (
              <p className="text-xs text-warning mt-2">
                Please select at least one project to mark as paid.
              </p>
            )}
            {selectedProjects.length > 0 && selectedProjects.length < allActiveProjects.length && (
              <p className="text-xs text-info mt-2">
                Partial payout: {selectedProjects.length} of {allActiveProjects.length} projects selected.
              </p>
            )}
          </div>

          <Separator />

          {/* Summary Card - now shows selected projects only */}
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
              <CardTitle className="text-xs sm:text-sm font-medium">Payout Summary</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm px-3 sm:px-6 pb-3 sm:pb-6">
              <div>
                <span className="text-muted-foreground">Total Revenue</span>
                <p className="font-semibold font-serif text-sm sm:text-base">{formatCurrency(calculations.totalRevenue)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Profit</span>
                <p className="font-semibold font-serif text-sm sm:text-base text-positive">{formatCurrency(calculations.totalProfit)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Operating Costs</span>
                <p className="font-semibold font-serif text-sm sm:text-base">{formatCurrency(calculations.totalOperatingCosts)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Selected Projects</span>
                <p className="font-semibold text-sm sm:text-base">{selectedProjects.length}</p>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Owner Distributions - now based on selected projects */}
          <div>
            <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3">Owner Distributions</h4>
            {selectedProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">Select projects to see distributions.</p>
            ) : (
              <div className="space-y-2">
                {ownerTotals.filter(o => o.total > 0).map(owner => (
                  <div key={owner.name} className="p-2 bg-muted/50 rounded-md">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{owner.name}</span>
                      <span className="font-semibold font-serif text-positive text-sm">{formatCurrency(owner.total)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {owner.profitShare > 0 && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs">
                          Profit: {formatCurrency(owner.profitShare)}
                        </Badge>
                      )}
                      {owner.operations > 0 && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs">
                          Ops: {formatCurrency(owner.operations)}
                        </Badge>
                      )}
                      {owner.tactical > 0 && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs">
                          Tac: {formatCurrency(owner.tactical)}
                        </Badge>
                      )}
                      {owner.kicker > 0 && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs">
                          Kick: {formatCurrency(owner.kicker)}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Specialist Distributions */}
          {calculations.specialistTacticalPayouts && calculations.specialistTacticalPayouts.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3">Specialist Distributions</h4>
                <div className="space-y-2">
                  {(() => {
                    // Aggregate by specialist name
                    const grouped: Record<string, { total: number; roles: string[] }> = {};
                    calculations.specialistTacticalPayouts.forEach(p => {
                      if (!grouped[p.name]) grouped[p.name] = { total: 0, roles: [] };
                      grouped[p.name].total += p.amount;
                      if (!grouped[p.name].roles.includes(p.role)) grouped[p.name].roles.push(p.role);
                    });
                    return Object.entries(grouped).sort((a, b) => b[1].total - a[1].total).map(([name, data]) => (
                      <div key={name} className="p-2 bg-muted/50 rounded-md">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{name}</span>
                          <span className="font-semibold font-serif text-positive text-sm">{formatCurrency(data.total)}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {data.roles.map(role => (
                            <Badge key={role} variant="outline" className="text-[10px] sm:text-xs">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsPaid}
            disabled={isSubmitting || selectedProjects.length === 0}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Confirm & </span>Mark as Paid
                {selectedProjects.length > 0 && selectedProjects.length < allActiveProjects.length && (
                  <span className="ml-1">({selectedProjects.length})</span>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
